import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getToken, clearToken, authHeaders } from "@/lib/api";
import Logo from "@/components/Logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MenuTab from "@/components/admin/MenuTab";
import SpecialsTab from "@/components/admin/SpecialsTab";
import GalleryTab from "@/components/admin/GalleryTab";
import ScheduleTab from "@/components/admin/ScheduleTab";
import EnquiriesTab from "@/components/admin/EnquiriesTab";
import { LogOut } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    axios
      .get(`${API}/auth/me`, authHeaders())
      .then((res) => setUser(res.data))
      .catch(() => {
        clearToken();
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-disco-void flex items-center justify-center">
        <p className="text-zinc-500 text-sm tracking-widest uppercase animate-pulse">Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-disco-void">
      <header className="sticky top-0 z-40 bg-black/50 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo size={36} />
          <div className="flex items-center gap-4">
            <span data-testid="admin-user-email" className="text-xs text-zinc-400 hidden sm:block">
              {user.email}
            </span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 text-zinc-300 text-xs font-semibold px-4 py-2 transition-colors duration-300 hover:border-disco-yellow hover:text-disco-yellow active:scale-95"
            >
              <LogOut size={13} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display uppercase tracking-wide text-4xl text-[#F8F8F8] mb-8">
          Cart <span className="neon-yellow">Control</span>
        </h1>

        <Tabs defaultValue="menu">
          <TabsList className="bg-disco-surface border border-white/10 mb-8">
            <TabsTrigger data-testid="admin-tab-menu" value="menu">Menu</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-specials" value="specials">Specials</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-gallery" value="gallery">Gallery</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-schedule" value="schedule">Schedule</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-enquiries" value="enquiries">Enquiries</TabsTrigger>
          </TabsList>
          <TabsContent value="menu"><MenuTab /></TabsContent>
          <TabsContent value="specials"><SpecialsTab /></TabsContent>
          <TabsContent value="gallery"><GalleryTab /></TabsContent>
          <TabsContent value="schedule"><ScheduleTab /></TabsContent>
          <TabsContent value="enquiries"><EnquiriesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
